import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Box, Collapse, Container, Divider, GlobalStyles, ListItemButton, ListItemIcon, ListItemText, NoSsr, Stack, Typography, styled } from '@mui/material'
import {
  Style as StyleIcon,
  ShoppingBasket as ShoppingBasketIcon,
  PhotoLibrary as PhotoLibraryIcon,
  PhotoCamera as PhotoCameraIcon,
  EditLocation as EditLocationIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  LabelOutlined as LabelOutlinedIcon,
  AddBox as AddBoxIcon,
  LockOutlined as LockOutlinedIcon,
  AccountCircle as AccountCircleIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  ExpandMoreRounded as ExpandMoreRoundedIcon
} from '@mui/icons-material'
import { Outlet, useLocation, NavLink, matchPath } from 'react-router'
import { useAppSelector } from '../Common/_hooks/useAppSelector'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import type UserStoreStore from '../UserData/_stores/Store/UserStoreStore'
import StoreNav from './StoreNav'
import PageLoader from '../Common/Loader/PageLoader'
import AccountMobileNav from './AccountMobileNav'
import sidebarOrnamentsUrl from '@resources/img/account/sidebar-ornaments.svg?url'

const accountMobileNavHeight = 58
const accountMobileNavContextLabelHeight = 24
const liveChatMobileNavGap = 16
const liveChatMobileBottomOffsetCssVariable = '--aow-live-chat-mobile-bottom-offset'

function getAccountMobileNavOffset(hasContextLabel: boolean, extraOffset = 0) {
  const navOffset = accountMobileNavHeight
    + (hasContextLabel ? accountMobileNavContextLabelHeight : 0)
    + extraOffset

  return `calc(${navOffset}px + env(safe-area-inset-bottom))`
}

const Nav = styled(Stack)(({ theme }) => ({
  '--account-nav-sticky-offset': '32px',
  '--account-nav-footer-overlap': '1px',
  width: '300px',
  padding: theme.spacing(3.5, 0, 4),
  flexShrink: 0,
  backgroundImage: 'url(' + sidebarOrnamentsUrl + ')',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom right',
  backgroundSize: '50%',
  position: 'sticky',
  top: 'var(--account-nav-sticky-offset)',
  height: 'calc(100vh - var(--account-nav-sticky-offset) + var(--account-nav-footer-overlap))',
  overflow: 'auto',
  // transition: 'box-shadow 250ms',
  boxShadow: 'none',

  '@supports (height: 100dvh)': {
    height: 'calc(100dvh - var(--account-nav-sticky-offset) + var(--account-nav-footer-overlap))'
  },

  '&.white-page': {
    boxShadow: '-120px 0 0 rgba(255, 255, 255, 1), 0 0 100px rgba(0, 0, 0, 0.1)'
  },

  [theme.breakpoints.down('lg')]: {
    width: '260px'
  }
}))

const NavList = styled('div')(({ theme }) => ({
  padding: theme.spacing(1, 0, 2, 0),

  '.MuiListItemButton-root': {
    paddingLeft: theme.spacing(3)
  },

  'a.active': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)'
  }
}))

const ListHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 2, 1, 3),
  fontSize: '1.5rem',
  fontWeight: '400',
  lineHeight: 'normal',
  textTransform: 'none'
}))

export const NavIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: theme.spacing(5)
}))

const MainWrapper = styled('div')(({ theme }) => ({
  // transition: 'background-image 250ms',
  backgroundImage: 'linear-gradient(90deg, #fff 0%, #fff 50%, #f0f0f0 50%, #f0f0f0 100%)',
  backgroundColor: '#fff',

  '&.white-page': {
    backgroundImage: 'none'
  }
}))

export default function Account() {
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')
  const location = useLocation()

  const stores = useAppSelector(state => state.get('userData').get('stores'))
  const settingsPath = tR('/account/settings')

  const getOpenedStoreSlug = useCallback((pathname: string) => {
    const currentStore = stores.valueSeq().find(store => {
      const storeSlug = store.get('slug')
      const storePath = tR('/account/stores/{{store}}', { store: storeSlug })
      const setupPath = tR('/account/stores/connect/{{store}}', { store: storeSlug })

      return pathname.indexOf(storePath) === 0
        || pathname === setupPath
    })

    return currentStore?.get('slug') ?? null
  }, [stores, tR])

  const [settingsOpened, setSettingsOpened] = useState(location.pathname.indexOf(settingsPath) === 0)
  const [openedStoreSlug, setOpenedStoreSlug] = useState<string | null>(() => getOpenedStoreSlug(location.pathname))

  useEffect(() => {
    setSettingsOpened(location.pathname.indexOf(settingsPath) === 0)
  }, [location.pathname, settingsPath])

  useEffect(() => {
    setOpenedStoreSlug(getOpenedStoreSlug(location.pathname))
  }, [getOpenedStoreSlug, location.pathname])

  const isWhitePage = useMemo(() => {
    return location.pathname === tR('/account/products')
      || !!matchPath(tR('/account/stores/:storeSlug/products'), location.pathname)
  }, [location.pathname, tR])

  const hasMobileNavContextLabel = useMemo(() => {
    return getOpenedStoreSlug(location.pathname) !== null
      || location.pathname.indexOf(settingsPath) === 0
  }, [getOpenedStoreSlug, location.pathname, settingsPath])
  const accountMobileNavOffset = getAccountMobileNavOffset(hasMobileNavContextLabel)
  const accountLiveChatMobileBottomOffset = getAccountMobileNavOffset(hasMobileNavContextLabel, liveChatMobileNavGap)

  const onSettingsToggle = useCallback(() => {
    setSettingsOpened(value => !value)
  }, [])

  const onStoreToggle = useCallback((storeSlug: string) => {
    setOpenedStoreSlug(value => value === storeSlug ? null : storeSlug)
  }, [])

  const renderStoreListItem = useCallback((store: UserStoreStore) => {
    const storeSlug = store.get('slug')

    return <StoreNav
      key={store.get('id')}
      store={store}
      opened={openedStoreSlug === storeSlug}
      onToggle={onStoreToggle}
    />
  }, [onStoreToggle, openedStoreSlug])

  return <>
    <GlobalStyles
      styles={(theme) => ({
        [theme.breakpoints.down('md')]: {
          html: {
            [liveChatMobileBottomOffsetCssVariable]: accountLiveChatMobileBottomOffset,
          }
        }
      })}
    />
    <Helmet
      titleTemplate={t('%s | Account | Art of Where')}
    >

    </Helmet>
    <MainWrapper
      className={isWhitePage ? 'white-page' : ''}
      sx={{
        '--account-mobile-nav-offset': {
          xs: accountMobileNavOffset,
          md: '0px',
        }
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters
      >
        <Stack direction="row">
          <Nav
            divider={<Divider />}
            className={isWhitePage ? 'white-page' : ''}
            sx={{
              display: {
                xs: 'none',
                md: 'block'
              }
            }}
          >
            <NavList>
              <ListHeader variant="h3">{t('Account')}</ListHeader>
              <ListItemButton component={NavLink} to={tR('/account/products')}>
                <NavIcon><StyleIcon /></NavIcon>
                <ListItemText>{t('Products')}</ListItemText>
              </ListItemButton>
              <ListItemButton component={NavLink} to={tR('/account/orders')}>
                <NavIcon><ShoppingBasketIcon /></NavIcon>
                <ListItemText>{t('Orders')}</ListItemText>
              </ListItemButton>
              <ListItemButton component={NavLink} to={tR('/account/images')}>
                <NavIcon><PhotoLibraryIcon /></NavIcon>
                <ListItemText>{t('Image Library')}</ListItemText>
              </ListItemButton>
              <ListItemButton component={NavLink} to={tR('/account/branding-options')}>
                <NavIcon><LabelOutlinedIcon /></NavIcon>
                <ListItemText>{t('Branding options')}</ListItemText>
              </ListItemButton>
              <ListItemButton component={NavLink} to={tR('/account/photo-lab')}>
                <NavIcon><PhotoCameraIcon /></NavIcon>
                <ListItemText>{t('Photo Lab')}</ListItemText>
              </ListItemButton>
            </NavList>
            <NavList>
              <ListHeader variant="h3">{t('Stores')}</ListHeader>
              {stores.valueSeq().map(renderStoreListItem)}
              <ListItemButton component={NavLink} to={tR('/account/stores')} end>
                <NavIcon><AddBoxIcon /></NavIcon>
                <ListItemText>{t('Add Store')}</ListItemText>
              </ListItemButton>
            </NavList>
            <NavList>
              <ListItemButton onClick={onSettingsToggle}>
                <ListHeader
                  variant="h3"
                  sx={{
                    p: 0,
                    flexGrow: 1
                  }}
                >{t('Settings')}</ListHeader>
                <ExpandMoreRoundedIcon
                  fontSize="large"
                  sx={{
                    transform: settingsOpened ? 'scaleY(-1)' : 'scaleY(1)',
                    transition: 'transform 250ms'
                  }}
                />
              </ListItemButton>
              <Collapse in={settingsOpened} timeout="auto">
                <ListItemButton component={NavLink} to={tR('/account/settings/info')}>
                  <NavIcon><AccountCircleIcon /></NavIcon>
                  <ListItemText>{t('Personal info')}</ListItemText>
                </ListItemButton>
                <ListItemButton component={NavLink} to={tR('/account/settings/notifications')}>
                  <NavIcon><NotificationsOutlinedIcon /></NavIcon>
                  <ListItemText>{t('Notifications')}</ListItemText>
                </ListItemButton>
                <ListItemButton component={NavLink} to={tR('/account/settings/security')}>
                  <NavIcon><LockOutlinedIcon /></NavIcon>
                  <ListItemText>{t('Security')}</ListItemText>
                </ListItemButton>
                <ListItemButton component={NavLink} to={tR('/account/settings/addresses')}>
                  <NavIcon><EditLocationIcon /></NavIcon>
                  <ListItemText>{t('Addresses')}</ListItemText>
                </ListItemButton>
                <ListItemButton component={NavLink} to={tR('/account/settings/credit-cards')}>
                  <NavIcon><CreditCardIcon /></NavIcon>
                  <ListItemText>{t('Credit cards')}</ListItemText>
                </ListItemButton>
                <ListItemButton component={NavLink} to={tR('/account/settings/tax-numbers')}>
                  <NavIcon><AccountBalanceIcon /></NavIcon>
                  <ListItemText>{t('Tax numbers')}</ListItemText>
                </ListItemButton>
              </Collapse>
            </NavList>
          </Nav>
          <Box component="div" sx={{
            flexGrow: 1,
            minWidth: 0,
            pt: {
              xs: 2,
              md: 5,
            },
            px: {
              xs: 2,
              md: 3,
            },
            pb: {
              xs: 'calc(112px + env(safe-area-inset-bottom))',
              md: 6,
            },
            // transition: 'background-color 250ms',
            backgroundColor: isWhitePage ? '#ffffff' : '#f0f0f0'
          }}>
            <NoSsr>
              <Suspense fallback={<PageLoader/>}>
                <Outlet />
              </Suspense>
            </NoSsr>
          </Box>
        </Stack>
      </Container>
    </MainWrapper>
    <AccountMobileNav />
  </>
}
